import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, RevealStagger, StaggerItem } from "../components/mindscope/Reveal";

const audiences = [
  {
    label: "Industry Leaders",
    body: "Founders, promoters and board members of listed and mid-market enterprises seeking a capital partner with fiduciary discipline and long horizons.",
  },
  {
    label: "Visionaries",
    body: "First-generation entrepreneurs building durable Indian businesses — the bootstrapped, the bold, the purpose-led.",
  },
];

const identityPillars = [
  { label: "Compounding trust", desc: "Relationships tenured across cycles, not deals." },
  { label: "Rigour with restraint", desc: "Regulator-ready work, delivered without theatre." },
  { label: "Ascent, deliberate", desc: "Every mandate is a staged milestone, not a sprint." },
];

export default function About() {
  return (
    <main data-testid="about-page" className="bg-ms-paper text-ms-navy overflow-hidden">
      {/* Header */}
      <section className="pt-40 pb-16 bg-ms-navy text-white grain relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow mb-6">Chapter One</div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-[-0.03em]">
              Our <span className="italic text-ms-gold">Identity</span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Identity intro + pillars */}
      <section className="section-y bg-ms-paper relative">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-display italic text-3xl md:text-5xl text-ms-navy/85 leading-[1.1] max-w-4xl">
              Mindscope is a contemporary financial advisory collective —
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 font-body text-lg md:text-xl text-ms-navy/80 leading-relaxed max-w-3xl">
              built to translate ambition into capital, and capital into consequence. Our practice
              sits at the intersection of regulation, strategy and stewardship — where SEBI
              frameworks, board conscience and long-horizon founder intent must be reconciled into a
              single, actionable narrative.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-6 font-body text-lg md:text-xl text-ms-navy/80 leading-relaxed max-w-3xl">
              We are not a syndicate. We are a firm with a memory — one that shows up before the
              mandate and stays after the listing bell.
            </p>
          </Reveal>

          {/* Three pillars */}
          <RevealStagger className="mt-16 md:mt-20 grid md:grid-cols-3 gap-px bg-ms-navy/10">
            {identityPillars.map((p, i) => (
              <StaggerItem key={p.label}>
                <div className="bg-ms-paper p-8 md:p-10 h-full">
                  <div className="tick-num text-4xl md:text-5xl text-ms-gold-bright mb-4">
                    0{i + 1}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-ms-navy leading-tight mb-3">
                    {p.label}
                  </h3>
                  <p className="font-body text-ms-navy/70 leading-relaxed">{p.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Who We Are */}
      <section className="section-y bg-ms-navy text-white relative grain">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow mb-6">Who We Are</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-3xl">
              A <span className="italic text-ms-gold">collective</span> — not a corporation.
            </h2>
          </Reveal>
          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <Reveal delay={0.15} className="lg:col-span-6">
              <p className="font-body text-white/80 text-lg leading-relaxed">
                Mindscope is a contemporary financial advisory collective, quietly known among founders
                and family principals as a house that treats every mandate like it is being read into
                the record — because, one way or another, it will be.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="lg:col-span-6">
              <p className="font-body text-white/80 text-lg leading-relaxed">
                Our practice architecture is deliberately flat, deliberately small, and deliberately
                senior. Every client is served by principals — not by a pyramid.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="section-y bg-ms-cream relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <Reveal className="lg:col-span-7">
              <div className="eyebrow mb-6" style={{ color: "#0A1128" }}>
                <span style={{ color: "#0A1128" }}>Who We Serve</span>
              </div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-ms-navy">
                The <span className="italic">bootstrapped</span>. The <span className="italic">bold</span>. The <span className="italic">purpose-led</span>.
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.15}>
              <p className="font-body text-ms-navy/70 leading-relaxed">
                We choose our mandates the way our clients choose their capital — patiently, and with
                intent.
              </p>
            </Reveal>
          </div>

          <RevealStagger className="grid md:grid-cols-2 gap-6">
            {audiences.map((a, i) => (
              <StaggerItem key={a.label}>
                <div className={`p-10 md:p-12 h-full ${i === 0 ? "bg-white border border-ms-navy/10" : "bg-ms-navy text-white"}`}>
                  <div className={`font-display text-6xl mb-6 ${i === 0 ? "text-ms-gold-bright" : "text-ms-gold"}`}>
                    0{i + 1}
                  </div>
                  <h3 className={`font-display text-3xl mb-4 ${i === 0 ? "text-ms-navy" : "text-white"}`}>
                    {a.label}
                  </h3>
                  <p className={`font-body leading-relaxed ${i === 0 ? "text-ms-navy/70" : "text-white/75"}`}>
                    {a.body}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* CTA next chapter */}
      <section className="section-y bg-ms-navy text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          <Reveal>
            <div className="eyebrow mb-4">Continue</div>
            <h3 className="font-display text-4xl md:text-6xl leading-[0.95]">
              Chapter Two — <span className="italic text-ms-gold">Strategic Intent</span>
            </h3>
          </Reveal>
          <Link to="/vision" data-testid="about-next-chapter" className="btn-gold">
            Read Vision & Mission <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
